import { AppPageHeader } from './app-page-header';
import { memo, useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react';
import {
  BookTextIcon,
  ChevronsUpDownIcon,
  FileSearchIcon,
  Loader2Icon,
  LogOutIcon,
  MoreHorizontalIcon,
  MoonIcon,
  PenSquareIcon,
  PencilIcon,
  SunIcon,
  Trash2Icon,
  UsersIcon,
  Building2Icon,
  DatabaseZapIcon,
  SettingsIcon,
} from 'lucide-react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import type { ConversationDTO } from '@shared/schemas/chat';
import { toast } from 'sonner';
import {
  useDeleteConversation,
  useInfiniteConversationsList,
  useRenameConversation,
} from '@/features/chat/hooks/useConversation';
import {
  buildConversationPath,
  buildNewChatLocationState,
  buildNewChatPath,
  isChatExperiencePath,
  resolveSidebarChatNavActiveState,
} from '@/features/chat/chat-session-intent';
import {
  buildDocumentsLocationState,
  DOCUMENTS_PATH,
  forgetChatConversation,
  resolveSidebarChatSpace,
} from '@/features/chat/chat-space-navigation';
import { getChatErrorMessage } from '@/features/chat/chat-errors';
import { authClient } from '@/lib/auth-client';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useThemeCustomizer } from '@/lib/theme-customizer';
import { SITE_TITLE } from '@/lib/site-meta';
import { CompanySidebarLogo } from '@/features/company/components/CompanySidebarLogo';
import { SidebarToggleIcon } from '@/components/sidebar-toggle-icon';
import { useOrganizationAccess } from '@/features/organization/useOrganizationAccess';

type SessionData = NonNullable<ReturnType<typeof authClient.useSession>['data']>;
type SessionUserWithRole = SessionData['user'] & { role?: string | null };

type AppSidebarProps = {
  onSignOut: () => Promise<void>;
  session: SessionData;
};

/*
 * Collapse motion, after transitions.dev "Card resize" + "Panel reveal":
 * the panel width tweens 300ms on the smooth-out curve (components/ui/sidebar.tsx),
 * while the content keeps the expanded width and is only clipped, so nothing
 * re-wraps or reflows mid-animation. Labels leave fast (150ms) and come back once
 * the panel has started opening (80ms delay, 250ms). Everything keys off the
 * sidebar root `data-state` in CSS: toggling re-renders no conversation row.
 */
const SIDEBAR_SMOOTH_EASE_CLASS = 'ease-[cubic-bezier(0.22,1,0.36,1)]';
const SIDEBAR_FIXED_WIDTH_CLASS = 'w-(--sidebar-width)';
const SIDEBAR_LABEL_FADE_CLASS = cn(
  'transition-opacity delay-80 duration-250 motion-reduce:transition-none',
  SIDEBAR_SMOOTH_EASE_CLASS,
  'group-data-[state=collapsed]:opacity-0 group-data-[state=collapsed]:delay-0 group-data-[state=collapsed]:duration-150 group-data-[state=collapsed]:ease-out',
);

const primaryNavigation = [
  {
    href: '/settings',
    icon: SettingsIcon,
    label: 'Paramètres',
    testId: 'nav-settings',
  },
];

const administratorNavigation = [
  {
    href: '/admin/users',
    icon: UsersIcon,
    label: 'Utilisateurs',
    testId: 'nav-admin-users',
  },
  {
    href: '/admin/companies',
    icon: Building2Icon,
    label: 'Entreprises',
    testId: 'nav-admin-companies',
  },
];

const companyMembersNavigation = {
  href: '/company/members',
  icon: UsersIcon,
  label: 'Membres',
  testId: 'nav-company-members',
};

const companySourcesNavigation = {
  href: '/company/sources',
  icon: DatabaseZapIcon,
  label: 'Connecteurs',
  testId: 'nav-company-sources',
};

function getInitials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function getUserRole(session: SessionData) {
  return ((session.user as SessionUserWithRole).role ?? 'user').toLowerCase();
}

function getPageMeta(pathname: string) {
  if (pathname === '/chat' || pathname.startsWith('/chat/') || pathname === '/rag' || pathname.startsWith('/rag/')) {
    return {
      group: 'Chat',
      page: 'Conversation',
    };
  }

  if (pathname === '/settings') {
    return {
      group: 'Menu principal',
      page: 'Paramètres',
    };
  }

  if (pathname === '/documents') {
    return {
      group: 'Menu principal',
      page: 'Base documentaire',
    };
  }

  if (pathname === '/components') {
    return {
      group: 'Menu principal',
      page: 'Composants',
    };
  }

  if (pathname === '/admin/users/new') {
    return {
      group: 'Administrateur',
      parent: {
        href: '/admin/users',
        label: 'Utilisateurs',
      },
      page: 'Créer un utilisateur',
    };
  }

  if (pathname.startsWith('/admin/users')) {
    return {
      group: 'Administrateur',
      page: 'Utilisateurs',
    };
  }

  if (pathname.startsWith('/admin/companies')) {
    if (pathname.includes('/usage')) {
      return {
        group: 'Administrateur',
        parent: {
          href: '/admin/companies',
          label: 'Entreprises',
        },
        page: 'Usage entreprise',
      };
    }
    if (pathname.includes('/members/new')) {
      return {
        group: 'Administrateur',
        parent: {
          href: pathname.replace(/\/new$/u, ''),
          label: 'Membres de l’entreprise',
        },
        page: 'Créer un membre',
      };
    }
    if (pathname.includes('/members')) {
      return {
        group: 'Administrateur',
        parent: {
          href: '/admin/companies',
          label: 'Entreprises',
        },
        page: 'Membres ciblés',
      };
    }
    return {
      group: 'Administrateur',
      page: 'Entreprises',
    };
  }

  if (pathname.startsWith('/company/members')) {
    if (pathname === '/company/members/new') {
      return {
        group: 'Entreprise',
        parent: {
          href: '/company/members',
          label: 'Membres',
        },
        page: 'Créer un membre',
      };
    }
    return {
      group: 'Entreprise',
      page: 'Membres',
    };
  }

  if (pathname.startsWith('/company/sources')) {
    return {
      group: 'Entreprise',
      page: 'Connecteurs',
    };
  }

  return {
    group: 'Menu principal',
    page: 'Chat',
  };
}

function AppBrand() {
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === 'collapsed';

  return (
    <div
      className={cn(
        'relative flex h-10 min-h-10 max-h-10 w-full items-center',
        // Collapsed: the size-9 toggle button is intentionally wider than the
        // collapsed content box (same overflow allowance as the collapsed
        // "nouvelle conversation" button below, which isn't clipped either).
        // Only clip while expanded, where it hides the wordmark during its
        // width transition.
        isCollapsed ? undefined : 'overflow-hidden',
      )}
    >
      <div
        className={cn(
          'relative flex h-10 min-h-10 max-h-10 items-center transition-[width] duration-200 ease-linear',
          // When collapsed, take the same available width as the collapsed
          // "nouvelle conversation" button's wrapper so the symbol centers
          // the same way; reserve space on the right for the absolute
          // collapse button when expanded so the wordmark never sits under it.
          // No overflow-hidden here while collapsed: the button is deliberately
          // wider than this box (see outer wrapper above) and must not be clipped.
          isCollapsed ? 'w-full' : 'min-w-0 flex-1 overflow-hidden pe-10',
        )}
      >
        {/* Wordmark — fade out on collapse, fade in on expand */}
        <Link
          to="/"
          aria-hidden={isCollapsed}
          tabIndex={isCollapsed ? -1 : undefined}
          title={SITE_TITLE}
          className={cn(
            'flex h-10 w-full min-w-0 items-center overflow-hidden rounded-xl transition-opacity duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none',
            isCollapsed
              ? 'pointer-events-none absolute inset-0 opacity-0'
              : 'relative opacity-100 delay-75 motion-reduce:delay-0',
          )}
        >
          <CompanySidebarLogo className="max-w-full" />
        </Link>

        {/* Symbol — fades in as the wordmark disappears. Pinned to the icon rail
            (-2px from the 8px header padding puts its 36px box centred on the 48px
            collapsed width) instead of centred in this box, so it stays still while
            the clipped, fixed-width header animates. Matches the collapsed
            "nouvelle conversation" button (size-9, rounded-xl). */}
        <button
          type="button"
          onClick={toggleSidebar}
          aria-hidden={!isCollapsed}
          tabIndex={isCollapsed ? 0 : -1}
          aria-label="Ouvrir la barre latérale"
          title="Ouvrir la barre latérale"
          className={cn(
            'group/brand absolute -start-0.5 top-1/2 inline-flex size-9 -translate-y-1/2 items-center justify-center rounded-xl text-sidebar-foreground outline-none transition-[opacity,background-color] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-sidebar-accent focus-visible:border-ring focus-visible:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-ring/25 motion-reduce:transition-none',
            isCollapsed
              ? 'opacity-100 delay-75 motion-reduce:delay-0'
              : 'pointer-events-none opacity-0',
          )}
        >
          {/* Symbol shown at rest; swaps to the open icon on hover/focus, without resizing the 36x36 hit area. */}
          <CompanySidebarLogo
            compact
            className="transition-opacity duration-150 group-hover/brand:opacity-0 group-focus-visible/brand:opacity-0 motion-reduce:transition-none"
          />
          <SidebarToggleIcon
            className="pointer-events-none absolute text-muted-foreground opacity-0 transition-opacity duration-150 group-hover/brand:opacity-100 group-focus-visible/brand:opacity-100 motion-reduce:transition-none"
          />
        </button>
      </div>

      {/* Pinned top/end — no translateY, so height changes can't bounce the control. */}
      <Button
        type="button"
        variant="ghost"
        size="icon-lg"
        onClick={toggleSidebar}
        aria-hidden={isCollapsed}
        tabIndex={isCollapsed ? -1 : undefined}
        className={cn(
          // Kill global active:translate-y-px — it looked like a vertical bounce on collapse.
          'absolute end-0 top-0 z-10 rounded-xl text-muted-foreground transition-opacity duration-150 ease-out hover:bg-sidebar-accent hover:text-sidebar-foreground active:translate-y-0 motion-reduce:transition-none',
          isCollapsed ? 'pointer-events-none opacity-0' : 'opacity-100',
        )}
        aria-label="Réduire la barre latérale"
        title="Réduire la barre latérale"
      >
        <SidebarToggleIcon />
      </Button>
    </div>
  );
}

function groupConversationsByPeriod(items: ConversationDTO[]) {
  const now = new Date();
  const groups = new Map<string, ConversationDTO[]>();

  items.forEach((conversation) => {
    const lastMessageAt = new Date(conversation.lastMessageAt);
    const diffInDays = Math.floor((now.getTime() - lastMessageAt.getTime()) / (1000 * 60 * 60 * 24));
    const key = diffInDays === 0 ? 'Aujourd’hui' : diffInDays <= 7 ? '7 derniers jours' : 'Plus ancien';
    const current = groups.get(key) ?? [];
    current.push(conversation);
    groups.set(key, current);
  });

  return Array.from(groups.entries());
}

type ConversationSidebarItemProps = {
  conversation: ConversationDTO;
  /** Only meaningful for the row being edited; other rows get an empty string. */
  draftTitle: string;
  isDeleting: boolean;
  isEditing: boolean;
  isRenaming: boolean;
  onDelete: (conversation: ConversationDTO) => void;
  onDraftTitleChange: (value: string) => void;
  onNavigate: () => void;
  onRenameSubmit: (conversation: ConversationDTO, title: string) => void;
  onStartRename: (conversation: ConversationDTO) => void;
};

/*
 * Memoised with stable callbacks and no sidebar-context subscription: toggling
 * the sidebar (or typing a new title in another row) does not re-render the
 * potentially long conversation list.
 */
const ConversationSidebarItem = memo(function ConversationSidebarItem({
  conversation,
  draftTitle,
  isDeleting,
  isEditing,
  isRenaming,
  onDelete,
  onDraftTitleChange,
  onNavigate,
  onRenameSubmit,
  onStartRename,
}: ConversationSidebarItemProps) {
  const location = useLocation();
  const href = buildConversationPath(
    conversation.id,
    conversation.ragEnabled ? 'document-search' : 'general',
  );
  const isActive = location.pathname === href;

  if (isEditing) {
    return (
      <SidebarMenuItem>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onRenameSubmit(conversation, draftTitle);
          }}
          className="mx-3 rounded-2xl border border-primary/25 bg-sidebar-accent/60 p-2"
        >
          <Input
            value={draftTitle}
            onChange={(event) => onDraftTitleChange(event.target.value)}
            autoFocus
            className="h-9 border-border bg-background text-sm"
            placeholder="Titre de conversation"
          />
          <div className="mt-2 flex items-center justify-end gap-2">
            <Button type="submit" size="sm" disabled={!draftTitle.trim() || isRenaming}>
              {isRenaming ? <Loader2Icon className="size-3.5 animate-spin" /> : null}
              Enregistrer
            </Button>
          </div>
        </form>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <div
        data-active={isActive}
        className="group/item flex h-9 items-center rounded-[10px] text-sidebar-foreground transition-colors data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground hover:bg-sidebar-accent dark:hover:bg-sidebar-accent/70 hover:text-sidebar-accent-foreground"
      >
        <NavLink
          to={href}
          data-testid={`conversation-link-${conversation.id}`}
          onClick={onNavigate}
          className="flex h-full min-w-0 flex-1 items-center rounded-l-[10px] px-2.5 text-sm leading-5"
        >
          <span className="truncate">{conversation.title ?? 'Nouvelle conversation'}</span>
        </NavLink>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex size-8 shrink-0 items-center justify-center rounded-r-[10px] text-sidebar-foreground/50 opacity-0 transition group-hover/item:opacity-100 group-data-[active=true]/item:opacity-100 hover:bg-sidebar-accent/80 hover:text-sidebar-accent-foreground"
            >
              <MoreHorizontalIcon className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 rounded-2xl">
            <DropdownMenuItem onSelect={() => onStartRename(conversation)}>
              <PencilIcon />
              Renommer
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => onDelete(conversation)}
              disabled={isDeleting}
              variant="destructive"
            >
              {isDeleting ? <Loader2Icon className="size-4 animate-spin" /> : <Trash2Icon />}
              Supprimer
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </SidebarMenuItem>
  );
});

function ConversationsSidebarSection({ ownerId }: { ownerId: string }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile, setOpenMobile, state } = useSidebar();
  const isCollapsed = state === 'collapsed' && !isMobile;
  const conversationsQuery = useInfiniteConversationsList(20);
  const deleteConversation = useDeleteConversation();
  const renameConversation = useRenameConversation();
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const [editingConversationId, setEditingConversationId] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [pendingDeleteConversation, setPendingDeleteConversation] = useState<ConversationDTO | null>(null);
  const isRagSpace = resolveSidebarChatSpace(location.pathname, ownerId, location.state) === 'rag';
  const items = useMemo(() => {
    const allItems = conversationsQuery.data?.pages.flatMap((page: { items: ConversationDTO[] }) => page.items) ?? [];

    return allItems.filter((conversation) => (
      isRagSpace ? conversation.ragEnabled : !conversation.ragEnabled
    ));
  }, [conversationsQuery.data?.pages, isRagSpace]);
  const groupedItems = useMemo(
    () => groupConversationsByPeriod(items),
    [items],
  );
  const { isDocumentSearchActive, isGeneralChatActive } = resolveSidebarChatNavActiveState(location.pathname);
  const isDocumentsActive = location.pathname === DOCUMENTS_PATH || location.pathname.startsWith(`${DOCUMENTS_PATH}/`);
  const documentsLabel = 'Base documentaire';
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = conversationsQuery;
  const sidebarNavButtonClassName = (isActive: boolean) => cn(
    'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground',
    isActive && 'bg-sidebar-accent font-medium',
  );
  const newSessionLabel = isRagSpace ? 'Nouvelle recherche' : 'Nouvelle discussion';
  const newSessionTestId = isRagSpace ? 'nav-new-document-search' : 'nav-new-chat';
  const isNewSessionActive = isRagSpace ? isDocumentSearchActive : isGeneralChatActive;
  const { mutateAsync: renameConversationAsync } = renameConversation;
  const closeMobileSidebar = useCallback(() => setOpenMobile(false), [setOpenMobile]);
  const handleStartRename = useCallback((conversation: ConversationDTO) => {
    setEditingConversationId(conversation.id);
    setDraftTitle(conversation.title ?? 'Nouvelle conversation');
  }, []);
  const handleRenameSubmit = useCallback(async (conversation: ConversationDTO, title: string) => {
    try {
      await renameConversationAsync({
        conversationId: conversation.id,
        input: {
          title: title.trim(),
        },
      });
      setEditingConversationId(null);
      setDraftTitle('');
    } catch (error) {
      toast.error(getChatErrorMessage(error));
    }
  }, [renameConversationAsync]);

  useEffect(() => {
    if (!hasNextPage || !loadMoreRef.current) {
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];

      if (!entry?.isIntersecting || isFetchingNextPage) {
        return;
      }

      void fetchNextPage();
    }, {
      rootMargin: '120px 0px',
    });

    observer.observe(loadMoreRef.current);

    return () => {
      observer.disconnect();
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  function handleNewSession() {
    navigate(buildNewChatPath(isRagSpace ? 'document-search' : 'general'), {
      state: buildNewChatLocationState(),
    });
  }

  return (
    <SidebarGroup>
      {/* Same element in both states: the icon sits 15px from the panel edge, on the
          collapsed rail axis, and the width tweens with the panel, so nothing jumps. */}
      <div className="-mx-0.5 flex flex-col pb-2">
        <Button
          type="button"
          variant="ghost"
          className={cn(
            'h-9 w-full justify-start gap-3 overflow-hidden rounded-xl px-[9px] transition-[width,background-color,color] duration-300 motion-reduce:transition-none group-data-[state=collapsed]:w-9',
            SIDEBAR_SMOOTH_EASE_CLASS,
            sidebarNavButtonClassName(isNewSessionActive),
          )}
          data-testid={newSessionTestId}
          onClick={handleNewSession}
          title={isCollapsed ? newSessionLabel : undefined}
          aria-current={isNewSessionActive ? 'page' : undefined}
        >
          {isRagSpace ? <FileSearchIcon className="size-4.5" /> : <PenSquareIcon className="size-4.5" />}
          <span className={cn('truncate', SIDEBAR_LABEL_FADE_CLASS)}>{newSessionLabel}</span>
        </Button>
        <Button
          asChild
          variant="ghost"
          className={cn(
            'h-9 w-full justify-start gap-3 overflow-hidden rounded-xl px-[9px] transition-[width,background-color,color] duration-300 motion-reduce:transition-none group-data-[state=collapsed]:w-9',
            SIDEBAR_SMOOTH_EASE_CLASS,
            sidebarNavButtonClassName(isDocumentsActive),
          )}
        >
          <Link
            to={DOCUMENTS_PATH}
            state={buildDocumentsLocationState(location.pathname)}
            data-testid="nav-documents"
            onClick={closeMobileSidebar}
            title={isCollapsed ? documentsLabel : undefined}
            aria-current={isDocumentsActive ? 'page' : undefined}
          >
            <BookTextIcon className="size-4.5" />
            <span className={cn('truncate', SIDEBAR_LABEL_FADE_CLASS)}>{documentsLabel}</span>
          </Link>
        </Button>
      </div>

      {/* The list stays mounted when collapsed (no unmount/remount of every row on
          toggle). It only fades (compositor opacity); `inert` takes it out of pointer,
          focus and the accessibility tree at once, without restyling every row. */}
      <div className={SIDEBAR_LABEL_FADE_CLASS} inert={isCollapsed}>
        {conversationsQuery.isLoading ? (
          <SidebarMenu>
            {Array.from({ length: 4 }).map((_, index) => (
              <SidebarMenuItem key={index}>
                <div className="mx-3 h-8 animate-pulse rounded-xl bg-sidebar-accent/60" />
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        ) : null}

        {conversationsQuery.isError ? (
          <div className="px-3 text-xs text-sidebar-foreground/60">
            Impossible de charger les conversations.
          </div>
        ) : null}

        {/* Keyed by space: switching Chat / Recherche replays the list entrance, nothing else. */}
        <div key={isRagSpace ? 'rag' : 'chat'} className="sidebar-space-enter">
          {groupedItems.map(([groupLabel, groupConversations]) => (
            <div key={groupLabel} className="space-y-0.5">
              <div className="truncate px-2.5 pt-3 text-xs font-medium tracking-[0.04em] text-sidebar-foreground/50 uppercase">
                {groupLabel}
              </div>
              <SidebarMenu className="gap-px">
                {groupConversations.map((conversation) => {
                  const isEditing = editingConversationId === conversation.id;

                  return (
                    <ConversationSidebarItem
                      key={conversation.id}
                      conversation={conversation}
                      draftTitle={isEditing ? draftTitle : ''}
                      isDeleting={deleteConversation.isPending && deleteConversation.variables === conversation.id}
                      isEditing={isEditing}
                      isRenaming={renameConversation.isPending && renameConversation.variables?.conversationId === conversation.id}
                      onDelete={setPendingDeleteConversation}
                      onDraftTitleChange={setDraftTitle}
                      onNavigate={closeMobileSidebar}
                      onRenameSubmit={handleRenameSubmit}
                      onStartRename={handleStartRename}
                    />
                  );
                })}
              </SidebarMenu>
            </div>
          ))}

          {items.length === 0 && !conversationsQuery.isLoading && !conversationsQuery.isError ? (
            <div className="px-3 pt-6 text-xs text-sidebar-foreground/60">
              {isRagSpace
                ? 'Aucune recherche documentaire pour le moment.'
                : 'Aucune conversation pour le moment.'}
            </div>
          ) : null}
        </div>

        <div ref={loadMoreRef} className="px-3 py-3">
          {conversationsQuery.isFetchingNextPage ? (
            <div className="flex items-center gap-2 text-xs text-sidebar-foreground/60">
              <Loader2Icon className="size-3.5 animate-spin" />
              Chargement d autres conversations...
            </div>
          ) : conversationsQuery.hasNextPage ? (
            <div className="text-xs text-sidebar-foreground/50">Scroll pour charger la suite</div>
          ) : null}
        </div>
      </div>

      <Dialog
        open={pendingDeleteConversation !== null}
        onOpenChange={(open) => {
          if (!open && !deleteConversation.isPending) {
            setPendingDeleteConversation(null);
          }
        }}
      >
        <DialogContent showCloseButton={!deleteConversation.isPending} className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle>Supprimer la conversation ?</DialogTitle>
            <DialogDescription>
              {`Cette action supprimera "${pendingDeleteConversation?.title ?? 'Nouvelle conversation'}" de manière définitive.`}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPendingDeleteConversation(null)}
              disabled={deleteConversation.isPending}
            >
              Annuler
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={!pendingDeleteConversation || deleteConversation.isPending}
              onClick={async () => {
                const conversationToDelete = pendingDeleteConversation;

                if (!conversationToDelete) {
                  return;
                }

                const deletedConversationPath = buildConversationPath(
                  conversationToDelete.id,
                  conversationToDelete.ragEnabled ? 'document-search' : 'general',
                );
                const deletedConversationHome = buildNewChatPath(
                  conversationToDelete.ragEnabled ? 'document-search' : 'general',
                );
                const isDeletingActiveConversation = location.pathname === deletedConversationPath;

                setPendingDeleteConversation(null);

                if (isDeletingActiveConversation) {
                  navigate(deletedConversationHome, { replace: true });
                }

                try {
                  await deleteConversation.mutateAsync(conversationToDelete.id);
                  forgetChatConversation(conversationToDelete.id);
                } catch (error) {
                  if (isDeletingActiveConversation) {
                    navigate(deletedConversationPath, { replace: true });
                  }

                  toast.error(getChatErrorMessage(error));
                }
              }}
            >
              {deleteConversation.isPending ? <Loader2Icon className="size-4 animate-spin" /> : <Trash2Icon />}
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarGroup>
  );
}

function NavUser({
  onSignOut,
  session,
}: AppSidebarProps) {
  const { isMobile } = useSidebar();
  const location = useLocation();
  const initials = useMemo(() => getInitials(session.user.name), [session.user.name]);
  const { effectiveMode, setMode } = useThemeCustomizer();
  const isAdmin = getUserRole(session) === 'admin';
  const organizationAccess = useOrganizationAccess();
  const companyNavigation = [
    ...(organizationAccess.canManageMembers ? [companyMembersNavigation] : []),
    ...(organizationAccess.canManageDatasource ? [companySourcesNavigation] : []),
  ];
  const navigationItems = [...primaryNavigation, ...companyNavigation];
  const adminNavigationItems = isAdmin ? administratorNavigation : [];

  const renderNavigationItem = (
    item: (typeof primaryNavigation)[number] & { badge?: string; disabled?: boolean },
  ) => {
    if (item.disabled) {
      return (
        <DropdownMenuItem
          key={item.href}
          data-testid={item.testId}
          disabled
          // Un item désactivé ne doit ni naviguer ni fermer le menu.
          onSelect={(event) => event.preventDefault()}
        >
          <item.icon />
          {item.label}
          {item.badge ? <Badge className="ml-auto">{item.badge}</Badge> : null}
        </DropdownMenuItem>
      );
    }

    const isActive = item.href === '/'
      ? location.pathname === item.href
      : location.pathname.startsWith(item.href);

    return (
      <DropdownMenuItem key={item.href} asChild data-testid={item.testId}>
        <Link to={item.href} className={isActive ? 'font-medium text-foreground' : undefined}>
          <item.icon />
          {item.label}
        </Link>
      </DropdownMenuItem>
    );
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger data-testid="user-menu-trigger" asChild>
            {/* Plain button rather than SidebarMenuButton: its collapsed `!size-8` would
                shrink the card at the first frame. Here the card keeps its size (it is
                clipped by the panel), the text fades and the avatar glides onto the
                rail axis with a compositor-only translate/scale. */}
            <button
              type="button"
              data-sidebar="menu-button"
              data-size="lg"
              className={cn(
                'group/user flex h-14 w-full items-center gap-3 rounded-md px-3 text-left text-sm text-sidebar-foreground outline-none ring-sidebar-ring transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground motion-reduce:transition-none',
                'group-data-[state=collapsed]:hover:bg-transparent group-data-[state=collapsed]:focus-visible:ring-0 group-data-[state=collapsed]:active:bg-transparent group-data-[state=collapsed]:data-[state=open]:bg-transparent',
              )}
            >
              <Avatar
                className={cn(
                  'size-9 rounded-full ring-1 ring-sidebar-border ring-offset-2 ring-offset-sidebar transition-[translate,scale,box-shadow] duration-300 motion-reduce:transition-none',
                  SIDEBAR_SMOOTH_EASE_CLASS,
                  // 20px -> 6px left edge and 36px -> 32px: centred on the 48px rail.
                  'group-data-[state=collapsed]:-translate-x-3.5 group-data-[state=collapsed]:scale-[0.889] group-data-[state=collapsed]:ring-0 group-data-[state=collapsed]:ring-offset-0',
                  'group-data-[state=collapsed]:group-focus-visible/user:ring-2 group-data-[state=collapsed]:group-focus-visible/user:ring-sidebar-ring',
                )}
              >
                <AvatarImage src={session.user.image ?? undefined} alt={session.user.name} />
                <AvatarFallback className="bg-sidebar-accent font-semibold text-sidebar-foreground">{initials}</AvatarFallback>
              </Avatar>
              <div className={cn('grid min-w-0 flex-1 text-left text-sm leading-tight', SIDEBAR_LABEL_FADE_CLASS)}>
                <span className="truncate font-medium">{session.user.name}</span>
                <span className="truncate text-xs">{session.user.email}</span>
              </div>
              <ChevronsUpDownIcon className={cn('ml-auto size-4 shrink-0', SIDEBAR_LABEL_FADE_CLASS)} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            data-testid="user-menu-content"
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'top'}
            align="end"
            sideOffset={8}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-3 px-2 py-2 text-left text-sm">
                <Avatar className="size-9 ring-1 ring-border ring-offset-2 ring-offset-popover">
                  <AvatarImage src={session.user.image ?? undefined} alt={session.user.name} />
                  <AvatarFallback className="bg-muted font-semibold text-foreground">{initials}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold text-foreground">{session.user.name}</span>
                  <span className="mt-0.5 truncate text-xs text-muted-foreground">{session.user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {navigationItems.map(renderNavigationItem)}
            {adminNavigationItems.length > 0 ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel
                  data-testid="user-menu-admin-section"
                  className="px-2 pb-0.5 pt-1.5 text-[0.7rem] font-medium uppercase tracking-wider text-muted-foreground"
                >
                  Administrateur
                </DropdownMenuLabel>
                {adminNavigationItems.map(renderNavigationItem)}
              </>
            ) : null}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => setMode(effectiveMode === 'dark' ? 'light' : 'dark')}>
              {effectiveMode === 'dark' ? <SunIcon /> : <MoonIcon />}
              {effectiveMode === 'dark' ? 'Activer le mode clair' : 'Activer le mode sombre'}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="logout-action" onClick={() => void onSignOut()}>
              <LogOutIcon />
              Déconnexion
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function AppSidebarLayout({
  children,
  onSignOut,
  session,
}: PropsWithChildren<AppSidebarProps>) {
  const location = useLocation();
  const { effectiveMode } = useThemeCustomizer();
  const isDarkMode = effectiveMode === 'dark';
  const pageMeta = getPageMeta(location.pathname);
  const isChatRoute = isChatExperiencePath(location.pathname);


  return (
    <SidebarProvider>
      <Sidebar
        collapsible="icon"
        className={cn(
          '[&_[data-sidebar=sidebar]]:bg-sidebar',
          isDarkMode
            ? '[&_[data-sidebar=sidebar]]:bg-[#171717] [&_[data-slot=sidebar-container]]:border-[#2f2f2f]'
            : '[&_[data-slot=sidebar-container]]:border-transparent [&_[data-slot=sidebar-container]]:border-r-0',
        )}
      >
        {/* Header, content and footer keep the expanded width in both states; the
            panel clips them while its width animates, so no row ever re-wraps. */}
        <SidebarHeader className={SIDEBAR_FIXED_WIDTH_CLASS}>
          <AppBrand />
        </SidebarHeader>
        <SidebarContent className={cn(SIDEBAR_FIXED_WIDTH_CLASS, 'contain-strict')}>
          <ConversationsSidebarSection ownerId={session.user.id} />
        </SidebarContent>
        {/* Le dégradé prolonge le fond de la sidebar au-dessus du footer pour
            estomper la liste au lieu de la couper net sur le bouton utilisateur. */}
        <SidebarFooter className={cn(SIDEBAR_FIXED_WIDTH_CLASS, 'relative before:pointer-events-none before:absolute before:inset-x-0 before:-top-8 before:h-8 before:bg-gradient-to-t before:from-sidebar before:to-transparent before:transition-opacity before:duration-150 group-data-[collapsible=icon]:before:opacity-0 motion-reduce:before:transition-none')}>
          <NavUser onSignOut={onSignOut} session={session} />
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>
      <SidebarInset className="route-stage">
        {isChatRoute ? null : (
          <AppPageHeader ownerId={session.user.id} pageMeta={pageMeta} />
        )}
        <div className={cn('flex min-h-0 flex-1 overflow-hidden bg-background', isDarkMode && 'bg-[#212121]')}>
          {isChatRoute ? (
            <div className="app-content-shell app-content-shell--chat flex min-w-0 flex-1 flex-col overflow-hidden p-0">
              {children}
            </div>
          ) : (
            /* !block sur l'enfant du viewport : Radix pose un wrapper display:table qui
               s'élargit avec les tableaux larges au lieu de les contraindre — en bloc,
               la largeur est bornée et l'overflow-x des tableaux reste interne. */
            <ScrollArea className="app-content-shell min-h-0 min-w-0 flex-1 overflow-hidden [&_[data-slot=scroll-area-viewport]>div]:!block">
              <div className="flex min-w-0 flex-col gap-6 p-6 lg:p-8">
                {children}
              </div>
            </ScrollArea>
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
